import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  AlignItems,
  Box,
  Field,
  FormFieldStatus,
  Hint,
  Label,
  Option,
  Select,
} from "@gocardless/flux-react";
import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CreditorType } from "@gocardless/api/dashboard/types";

import { creditorTypeList } from "../../../consts";
import { SignupFormData, SignUpFormField } from "../../consts";

import { getInputErrorStatus } from "src/utils/inputValidation";
import { FieldLevelError } from "src/common/api";
import { CountryCodeSupported } from "src/common/country";
import { TrackingEvent } from "src/common/trackingEvents";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import { UnsupportedCreditorTypeBanner } from "src/components/routes/Setup/common/components/UnsupportedCreditorTypeBanner";
import { useOptimizelyVariation } from "src/technical-integrations/optimizely/useOptimizelyVariation";
import { OptimizelyFlag } from "src/technical-integrations/optimizely/constants";

interface CreditorTypeDropdownProps {
  apiError?: FieldLevelError;
  isSubmitting: boolean;
  hasCreditorTypeError: (
    credGeo: CountryCodeSupported,
    credType: CreditorType
  ) => boolean;
}

export const CreditorTypeDropdown = ({
  apiError,
  isSubmitting,
  hasCreditorTypeError: isCreditorTypeError,
}: CreditorTypeDropdownProps) => {
  const { sendEvent } = useSegment();

  const { i18n } = useLingui();

  const {
    register,
    formState: { errors },
    trigger,
  } = useFormContext<SignupFormData>();

  const ErrorMessage = {
    required: i18n._(
      t({
        id: "sign-up.form.creditor_type.field-required-warning",
        message: "Business type is required",
      })
    ),
    hasCreditorTypeError: i18n._(
      t({
        id: "sign-up.form.creditor-and-geo.warning",
        message:
          "Unfortunately we are not able to serve this business type in this country.",
      })
    ),
  };

  const [creditorValue, setCreditorValue] = useState<string>("");

  const geoValue = useWatch({ name: "geo" });

  const generateCreditorTypeOptions = (creditorType: typeof CreditorType) =>
    Object.values(creditorType).map((type) => (
      <Option key={type} value={type} data-testid={type}>
        {creditorTypeList(i18n)[type as CreditorType]}
      </Option>
    ));

  const creditorTypeOptions = generateCreditorTypeOptions(CreditorType);

  const hasCreditorTypeError =
    creditorValue === ""
      ? false
      : isCreditorTypeError(geoValue, creditorValue as CreditorType);

  const sendFormFieldInteractionEvent = () => {
    sendEvent(TrackingEvent.SIGNUP_FORM_FIELD_INTERACTION, {
      label: SignUpFormField.creditor_type,
      type: "Blur",
    });
  };

  const { isFlagEnabled: isVerificationsUnsupportedEntityType } =
    useOptimizelyVariation({
      flag: OptimizelyFlag.VERIFICATIONS_UNSUPPORTED_ENTITY_TYPE,
    });

  useEffect(() => {
    if (creditorValue) {
      trigger(SignUpFormField.creditor_type);
    }
  }, [geoValue, creditorValue, trigger]);

  return (
    <Field data-testid={SignUpFormField.creditor_type}>
      <Box layout="flex" alignItems={AlignItems.Center}>
        <Label htmlFor={SignUpFormField.creditor_type}>
          <Trans id="sign-up.form.creditor_type.label">Business type</Trans>
        </Label>
      </Box>
      <Select
        id={SignUpFormField.creditor_type}
        data-testid="select_creditor_type"
        defaultValue="Choose your business type"
        status={
          isVerificationsUnsupportedEntityType && hasCreditorTypeError
            ? undefined
            : getInputErrorStatus(
                !isSubmitting &&
                  (!!errors.creditor_type ||
                    (!!creditorValue &&
                      isCreditorTypeError(
                        geoValue,
                        creditorValue as CreditorType
                      )))
              )
        }
        required
        {...register(SignUpFormField.creditor_type, {
          required: ErrorMessage.required,
          validate: () =>
            !hasCreditorTypeError || ErrorMessage.hasCreditorTypeError,
          onChange(e) {
            const targetValue = e.target.value;
            targetValue && setCreditorValue(targetValue);

            const hasError = isCreditorTypeError(geoValue, targetValue);

            sendEvent(TrackingEvent.SIGNUP_BUSINESS_TYPE_FIELD_UPDATED, {
              label: targetValue,
              ...(hasError && {
                error_type: "Unsupported business type",
                business_location: geoValue,
              }),
            });
          },
          onBlur: sendFormFieldInteractionEvent,
        })}
      >
        {[
          <Option key="default" value="">
            {t({
              id: "sign-up.form.creditor_type.default-value",
              message: "Choose your business type",
            })}
          </Option>,
          ...creditorTypeOptions,
        ]}
      </Select>
      {isVerificationsUnsupportedEntityType && hasCreditorTypeError ? (
        <Box spaceAbove={1}>
          <UnsupportedCreditorTypeBanner />
        </Box>
      ) : (
        errors?.creditor_type && (
          <Hint status={FormFieldStatus.Danger}>
            {errors?.creditor_type?.message}
          </Hint>
        )
      )}
      {apiError && (
        <Hint status={FormFieldStatus.Danger}>{apiError.message}</Hint>
      )}
    </Field>
  );
};
